import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { AccountDeactivatedError, fetchCurrentUser } from '../api/users';
import { AUTH_USER_STORAGE_KEY, getSessionToken, setSessionToken } from '../api/session';
import { revokeSession } from '../api/authSession';

const STORAGE_KEY = AUTH_USER_STORAGE_KEY;
const LOGOUT_REASON_KEY = 'techforce_logout_reason';

/** Read-and-clear a reason left behind when the account was signed out remotely. */
export function consumeLogoutReason(): 'deactivated' | null {
  try {
    const raw = sessionStorage.getItem(LOGOUT_REASON_KEY);
    if (raw) sessionStorage.removeItem(LOGOUT_REASON_KEY);
    return raw === 'deactivated' ? 'deactivated' : null;
  } catch {
    return null;
  }
}

function rememberDeactivatedLogout(): void {
  try {
    sessionStorage.setItem(LOGOUT_REASON_KEY, 'deactivated');
  } catch {
    // sessionStorage can be blocked; the login attempt will still show the message
  }
}

export interface AuthUser {
  id: number;
  username: string;
  email: string;
  /** Multiple roles; may be absent for legacy stored users (treat as single role). */
  roles?: string[];
  /** @deprecated Use roles; kept for backward compat with stored auth. */
  role?: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  /**
   * Server-verified session token from the last login. Null for people who were
   * already signed in before tokens existed — features that need it ask them to
   * sign in again rather than failing silently.
   */
  token: string | null;
  login: (user: AuthUser, token?: string | null) => void;
  logout: () => void;
  updateUser: (updates: Partial<AuthUser>) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function loadStoredUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AuthUser;
    if (!parsed || typeof parsed.id !== 'number' || !parsed.username) return null;
    // Normalize: ensure roles array for consumers (migrate legacy role)
    if (!Array.isArray(parsed.roles) || parsed.roles.length === 0) {
      if (parsed.role) parsed.roles = [parsed.role];
      else parsed.roles = ['sales'];
    }
    return parsed;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(loadStoredUser);
  const [token, setToken] = useState<string | null>(getSessionToken);

  // Sync roles (and profile) from server on load so sidebar/Employees reflect DB (e.g. after migration or admin grant).
  // Also the heartbeat that signs a deactivated account out of this browser: /api/users/me
  // returns 403 once TFRI deactivates them, even if they still have a localStorage user.
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    const sync = () => {
      fetchCurrentUser(user.id)
        .then((fresh) => {
          if (cancelled) return;
          setUser((prev) => {
            if (!prev) return null;
            const next = { ...prev, roles: fresh.roles, username: fresh.username, email: fresh.email };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
            return next;
          });
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          if (err instanceof AccountDeactivatedError) {
            rememberDeactivatedLogout();
            void revokeSession();
            setUser(null);
            setToken(null);
            localStorage.removeItem(STORAGE_KEY);
            setSessionToken(null);
          }
        });
    };
    sync();
    const intervalId = window.setInterval(sync, 15000);
    const onVis = () => {
      if (document.visibilityState === 'visible') sync();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [user?.id]);

  const login = useCallback((u: AuthUser, sessionToken?: string | null) => {
    setUser(u);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(u));
    setSessionToken(sessionToken ?? null);
    setToken(sessionToken ?? null);
  }, []);

  const logout = useCallback(() => {
    // Retire the token server-side too, so it cannot be reused if it was copied.
    void revokeSession();
    setUser(null);
    setToken(null);
    localStorage.removeItem(STORAGE_KEY);
    setSessionToken(null);
  }, []);

  const updateUser = useCallback((updates: Partial<AuthUser>) => {
    setUser((prev) => {
      if (!prev) return null;
      const next = { ...prev, ...updates };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, login, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
